/* eslint-disable @typescript-eslint/no-explicit-any */
import { reactive, readonly, Ref, ref, watch, computed, ComputedRef, DeepReadonly } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios, { AxiosResponse, CancelTokenSource } from "axios";
import { ALL_FILTER_KEYS, FilterOptions, FiltersData, SELECTS_FILTER_KEYS } from "./filters";
import { useHttp } from "../api";
import { EsArtworkDto } from "../dto/es-artwork";

export const SEARCH_API_URL = "/api/es_artworks";

const DEFAULT_ITEMS_PER_PAGE = 28; // chosen number is divisible by 4 so we can display full rows when rendering results
export const INITIAL_FILTER_OPTIONS: FilterOptions = {
    artType: { terms: [] },
    creatorsGenders: { terms: [] },
    creatorsNames: { terms: [] },
    creatorsNationalities: { terms: [] },
    period: { terms: [] },
    objectNames: { terms: [] },
    materialPosition: { terms: [] },
    techniques: { terms: [] },
    museum: { terms: [] },
    onDisplay: { terms: [] },
    hasReproductions: { terms: [] },
    sortByReproductions: { terms: [] },
    productionStartDate: {
        min: null,
        max: null,
    },
    productionEndDate: {
        min: null,
        max: null,
    },
};

export type UseSearchResults = DeepReadonly<EsArtworkDto[]>;
export type UseSearchFilterOptions = DeepReadonly<FilterOptions>;

export type SearchQueryReturnValue = Promise<AxiosResponse<EsArtworksApiResponse>>;

export interface UseSearch {
    /**
     * Filters data that is sent to api
     */
    filters: FiltersData;
    /**
     * Whether there is an api call ongoing
     */
    loading: Ref<boolean>;
    /**
     * Number of total results found for the search query. Before the search query is made this value is set to -1
     */
    totalItems: Ref<number>;
    /**
     * Filter options returned from api, before the query is made this object will have empty lists.
     */
    filterOptions: Ref<UseSearchFilterOptions>;
    /**
     * Results returned from api
     */
    results: Ref<UseSearchResults>;
    /**
     * Error, if query to api finished with an error, otherwise null. Can be used to display information to user that something went wrong.
     */
    error: Ref<any | null>;
    /**
     * Current page that was loaded from the api.
     */
    page: Ref<number>;
    /**
     * Whether user can load more options. It will check if the current request is in progress and if there are any more elements to load for
     * the current serach query.
     */
    canLoadMore: ComputedRef<boolean>;
    /**
     * Method that executes search, can be called to refresh data from the api
     */
    search: () => SearchQueryReturnValue;
    /**
     * Loads more data from the api (next page) and appends to results list
     */
    loadMore: () => SearchQueryReturnValue;
}

export interface UseSearchInitialResults {
    page: number;
    results: UseSearchResults;
    totalItems: number;
    filterOptions: UseSearchFilterOptions;
}

export type UseSearchInitialFilters = Omit<FiltersData, "lang">;

export interface UseSearchOptions {
    /**
     * Language that is used to make requests, since we might need to use a different language in some places useSearch is not tied to global language
     * instead it accepts a ref to the language, when updated it will make request again
     */
    language: Ref<string>;
    /**
     * Items displayed per page.
     *
     * This option is passed to the api and is not dynamic.
     */
    itemsPerPage?: number;
    /**
     * Initial response data that useSearch should use instead of making call to api.
     * If you want to set initial filters then @see {UseSearchOptions.initialFilters}
     *
     * If initial data is set but some persistance option is selected then this would be overriden.
     * See more in @see {UseSearchOptions.persistance}
     */
    initialResults?: UseSearchInitialResults;
    /**
     * Initial filters data that will be used for initial request, initial request is not performed if initial data is set.
     * @see {UseSearchOptions.initialResults}
     */
    initialFilters?: Readonly<UseSearchInitialFilters>;
}

export interface EsArtworksApiResponse {
    "hydra:totalItems": number;
    "hydra:member": EsArtworkDto[];
    "hydra:es:aggregation": FilterOptions;
}

export function useSearch({
    language,
    initialResults,
    initialFilters,
    itemsPerPage: optionItemsPerPage,
}: UseSearchOptions): UseSearch {
    const http = useHttp();
    const route = useRoute();
    const filters = reactive<FiltersData>({
        lang: language.value || "da",
        ...(initialFilters || {}),
    });

    const itemsPerPage: number = optionItemsPerPage || DEFAULT_ITEMS_PER_PAGE;
    const page = ref<number>(initialResults?.page || Number(route.query.page) || 1);
    const loading = ref<boolean>(false);
    const results = ref<UseSearchResults>(initialResults?.results || []);
    const totalItems = ref<number>(initialResults?.totalItems || -1);
    const filterOptions = ref<UseSearchFilterOptions>(initialResults?.filterOptions || INITIAL_FILTER_OPTIONS);
    const error = ref<any | null>(null);
    const canLoadMore = computed(() => {
        return !loading.value && page.value * itemsPerPage < totalItems.value;
    });

    let cancelToken: CancelTokenSource | undefined;
    // Function to query only records (without aggregations)
    const queryRecords = (): SearchQueryReturnValue => {
        if (cancelToken) cancelToken.cancel();
        cancelToken = axios.CancelToken.source();

        loading.value = true;

        const params = {
            ...filters,
            page: page.value,
            itemsPerPage,
            // Add a parameter to exclude aggregations
            withAggregations: false,
            sortByReproductions: true,
        };
        return http
            .get<EsArtworksApiResponse>(SEARCH_API_URL, { params, cancelToken: cancelToken.token })
            .then((res) => {
                loading.value = false;
                cancelToken = undefined;
                return res;
            })
            .catch((e: Error) => {
                cancelToken = undefined;

                if (!axios.isCancel(e)) {
                    loading.value = false;
                    error.value = e;
                }

                throw e;
            });
    };

    // Function to query aggregations only (without records)
    const queryAggregations = (): SearchQueryReturnValue => {
        const params = {
            ...filters,
            page: 1,
            itemsPerPage: 0, // We don't need to fetch any records
            withAggregations: true, // Only fetch aggregations
        };

        return http
            .get<EsArtworksApiResponse>(SEARCH_API_URL, { params })
            .then((res) => {
                return res;
            })
            .catch((e: Error) => {
                if (!axios.isCancel(e)) {
                    error.value = e;
                }
                throw e;
            });
    };

    const search = (): SearchQueryReturnValue => {
        // Start querying records first
        const recordsPromise = queryRecords().then((recordsRes) => {
            // Set totalItems as soon as the records response is received
            results.value = recordsRes.data["hydra:member"];
            return recordsRes;
        });

        // Meanwhile, query aggregations
        const aggregationsPromise = queryAggregations().then((aggregationsRes) => {
            // Set filterOptions once aggregations are received
            filterOptions.value = aggregationsRes.data["hydra:es:aggregation"];
            totalItems.value = aggregationsRes.data["hydra:totalItems"];
            return aggregationsRes;
        });

        // Return the first promise and wait for both to finish
        return Promise.all([aggregationsPromise, recordsPromise]).then(([recordsRes]) => {
            return recordsRes;
        });
    };

    // Load more data (only fetch records, no aggregations)
    const loadMore = (): SearchQueryReturnValue => {
        window.console.log("Loading more records");
        if (loading.value) return Promise.reject(new Error("Cannot load more when query is in progress!"));
        page.value = page.value + 1;

        return queryRecords().then((res) => {
            results.value = [...results.value, ...res.data["hydra:member"]];
            return res;
        });
    };

    // start searching when language is changed or filters change
    watch(
        () => [language.value, filters],
        () => {
            page.value = 1;
            search();
        },
        { deep: true }
    );

    if (!initialResults) {
        search().catch((err) => {
            if (axios.isCancel(err)) {
                return;
            }

            // Initial error failed, log it, error.value will have the error
            console.error("Initial search request failed with", err);
            throw err;
        });
    }

    return {
        filters,
        loading: readonly(loading),
        results: readonly(results),
        filterOptions: readonly(filterOptions),
        totalItems: readonly(totalItems),
        error: readonly(error),
        page: readonly(page),
        search,
        loadMore,
        canLoadMore,
    };
}

/**
 * Given query object parses it as filters object and returns filters which can be used as initialFilters in useSearch
 *
 * @param query {Record<string, string | string[]>} Query object
 * @returns {UseSearchInitialFilters} Initial filters object parsed from query object. Will contain only known keys to search and they
 * will have a correct type.
 */
export function filtersFromQuery(query: Record<string, string | string[]>): UseSearchInitialFilters {
    const filters: UseSearchInitialFilters = {};

    Object.keys(query)
        .filter((key) => ALL_FILTER_KEYS.includes(key))
        .forEach((key) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const value = query[key];

            if ((SELECTS_FILTER_KEYS as string[]).includes(key)) {
                filters[key] = Array.isArray(value) ? value : [value];
            } else if (value === "true") {
                filters[key] = true;
            } else if (value === "false") {
                filters[key] = false;
            } else {
                filters[key] = value;
            }
        });

    return filters;
}

/**
 * Given reactive filters object and page syncs it's value with route query
 * @param filters {FiltersData} - Reactive filters object
 */
export function syncFiltersAndPageWithQuery(filters: Readonly<FiltersData>, page: Ref<number>): void {
    const router = useRouter();
    const route = useRoute();
    // page=route.query.page

    watch(
        [filters, page],
        ([filters, page]) => {
            const normalizedFilters = Object.keys(filters).reduce((acc, key) => {
                if (typeof filters[key] === "boolean") {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    acc[key] = filters[key]!.toString();
                } else {
                    acc[key] = filters[key] as string | string[];
                }
                return acc;
            }, {} as Record<string, string | string[]>);

            const previousQuery = { ...route.query };

            Object.keys(previousQuery)
                .filter((key) => ALL_FILTER_KEYS.includes(key))
                .forEach((key) => {
                    delete previousQuery[key];
                });

            router.replace({ query: { ...previousQuery, ...normalizedFilters, page: page } });
        },
        { deep: true }
    );
}
